/* eslint-disable react/forbid-component-props, react/no-danger, sonarjs/cognitive-complexity */
import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import { CLAIM_CREDIT_UPDATE_STATUS } from '../../../brands/default/config';
import {
  REDEEM_CODE_BTN_CONFIRM,
  REDEEM_CODE_CONFIRM_MERCHANT,
  REDEEM_CODE_ORDER_ERROR,
  REDEEM_CODE_ORDER_NO,
  REDEEM_CODE_ORDER_REQUIRED,
  REDEEM_CODE_WOWCHER_CODE,
  REDEEM_CODE_WOWCHER_CODE_ERROR,
  REDEEM_CODE_WOWCHER_CODE_REQUIRED,
  PREFORMATTED_STRING,
  CREDIT_CLAIM_ERROR_CURRENCIES,
  CREDIT_CLAIM_FULL_CREDIT_REFUND,
  CREDIT_CLAIM_NON_REFUNDABLE,
  REDEEM_CODE_BTN_CANCEL,
  CREDIT_CLAIM_TOWARDS_DEAL,
  CREDIT_CLAIM_NOT_TOWARDS_DEAL_RETURN,
  REDEEM_CODE_BTN_DONE,
  CREDIT_CLAIM_NOT_TOWARDS_DEAL,
  CREDIT_CLAIM_REASON_ARIA_LABEL,
  CREDIT_CLAIM_REASON_SELECT_DEFAULT,
  CREDIT_CLAIM_DEFAULT_SORRY_MESSAGE,
} from '../../../brands/default/content/components/_generic';
import {
  SHOW_SECONDARY_BUTTON_REDDEM_FORM,
  SHOW_TERTIARY_BUTTON_REDDEM_FORM,
} from '../../../brands/default/features';
import { formatPrice, symbolFromCurrency } from '../../../helpers/currency';
import {
  validateNumber,
  validateCharacter,
} from '../../../helpers/validations';
import VouchersService from '../../../helpers/vouchersService';
import ThemeContext from '../../../providers/ThemeProvider';
import { logToSplunk } from '../axiosSplunk/axiosSplunk';
import ButtonForm from '../button/CTAButton';
import CustomNativeSelect from '../custom-native-select/CustomNativeSelect';
import InputForm from '../input/Input';
import Spinner from '../spinner/Spinner';

const initialState = {
  hasError: false,
  isChecked: false,
  isTouchOrderNo: false,
  isTouchWowcherCode: false,
  orderNo: '',
  showConfirmation: false,
  showRedeem: false,
  showRedeemForm: true,
  validationData: {},
  wowcherCode: '',
};
const RedeemForm = ({
  currency, // only passed in when calling on checkout
  hasLoadingSpinner,
  onCompleteCreditPage,
  redeemCreditOnComplete,
  showForm,
}) => {
  const theme = useContext(ThemeContext);
  const [redeemFormState, setRedeemFormState] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [refundReasonId, setRefundReasonId] = useState('');
  const [disableButton, setDisableButton] = useState(true);

  const towardsDeal = Boolean(currency);
  const wowcherOrVoucher =
    PREFORMATTED_STRING.brandTerminology.Wowcher_or_voucher;
  const {
    isChecked,
    orderNo,
    wowcherCode,
    isTouchOrderNo,
    isTouchWowcherCode,
    hasError,
    showConfirmation,
    showRedeem,
    showRedeemForm,
    validationData,
  } = redeemFormState;

  const showErrorMessage = (message) => {
    setRedeemFormState({ ...redeemFormState, hasError: message });
  };

  const updateOrderNo = (event) => {
    setRedeemFormState({
      ...redeemFormState,
      isTouchOrderNo: true,
      orderNo: event.target.value,
    });
  };

  const updateWowcherCode = (event) => {
    setRedeemFormState({
      ...redeemFormState,
      isTouchWowcherCode: true,
      wowcherCode: event.target.value,
    });
  };

  const handleCheckboxChange = () => {
    setRedeemFormState({ ...redeemFormState, isChecked: !isChecked });
  };

  const handleConfirmClick = async (event) => {
    event.preventDefault();
    hasLoadingSpinner(true);
    const data = await VouchersService.validateVoucher(orderNo, wowcherCode);
    hasLoadingSpinner(false);

    if (!data || (data && data.message)) {
      showErrorMessage(data?.message || CREDIT_CLAIM_DEFAULT_SORRY_MESSAGE);

      return;
    }

    // Invoke event callback function to deal with successful claim
    redeemCreditOnComplete({ creditDetails: data });

    if (
      currency &&
      data.voucher.currency.toLowerCase() !== currency.toLowerCase()
    ) {
      showErrorMessage(CREDIT_CLAIM_ERROR_CURRENCIES(wowcherOrVoucher));

      return;
    }

    setRedeemFormState({
      ...redeemFormState,
      hasError: false,
      showRedeem: true,
      showRedeemForm: false,
      validationData: data,
    });
  };

  const handleRedeemConfirmClick = async (event) => {
    event.preventDefault();
    hasLoadingSpinner(true);
    setIsLoading(true);

    const data = await VouchersService.updateStatus({
      orderNumber: orderNo,
      refundReasonId,
      status: CLAIM_CREDIT_UPDATE_STATUS,
      voucherCode: wowcherCode,
    });
    hasLoadingSpinner(false);
    if (data?.message) {
      showErrorMessage(data.message);

      return;
    }
    logToSplunk(
      {
        isWalletApplied: true,
        redeemUnUsedWowcherCode: true,
        type: 'unused voucher code redeemed',
      },
      'frontend logs',
    );
    // Invoke event callback function to deal with successful claim
    redeemCreditOnComplete({ creditDetails: validationData });

    // callback for claim credit page
    if (onCompleteCreditPage) onCompleteCreditPage();

    setRedeemFormState({
      ...redeemFormState,
      showConfirmation: true,
      showRedeem: false,
    });
    setIsLoading(false);
  };
  const handleCancelClick = () => {
    setRedeemFormState({
      ...redeemFormState,
      ...initialState,
    });
  };

  const isDisabled =
    !validateCharacter(wowcherCode) ||
    wowcherCode.length === 0 ||
    !validateNumber(orderNo) ||
    !isChecked;

  const handleSelectChange = (event) => {
    setRefundReasonId(event.target.value);
    if (event.target.value === '1') {
      setDisableButton(true);
    } else {
      setDisableButton(false);
    }
  };

  return (
    <div className="redeem-code__form">
      {isLoading && <Spinner />}
      {showForm && showRedeemForm && (
        <div>
          <label className="redeem-code__label" htmlFor="order-no">
            {REDEEM_CODE_ORDER_NO}
          </label>
          <InputForm
            bottomLined={Boolean(validateNumber(orderNo))}
            error={isTouchOrderNo && !validateNumber(orderNo)}
            errorText={
              orderNo.length === 0
                ? REDEEM_CODE_ORDER_REQUIRED
                : REDEEM_CODE_ORDER_ERROR
            }
            inputValue={orderNo}
            maxLength={9}
            name="order-no"
            onBlurFunction={updateOrderNo}
            onChangeFunction={updateOrderNo}
            type="text"
            valid={validateNumber(orderNo)}
          />
          <label className="redeem-code__label" htmlFor="wowcher-code">
            {REDEEM_CODE_WOWCHER_CODE(wowcherOrVoucher)}
          </label>
          <InputForm
            bottomLined={Boolean(validateNumber(wowcherCode))}
            error={
              isTouchWowcherCode &&
              (!validateCharacter(wowcherCode) || wowcherCode.length === 0)
            }
            errorText={
              wowcherCode.length === 0
                ? REDEEM_CODE_WOWCHER_CODE_REQUIRED(wowcherOrVoucher)
                : REDEEM_CODE_WOWCHER_CODE_ERROR(wowcherOrVoucher)
            }
            extraClasses="redeem-code__input"
            inputValue={wowcherCode}
            name="wowcher-code"
            onBlurFunction={updateWowcherCode}
            onChangeFunction={updateWowcherCode}
            type="text"
            valid={validateCharacter(wowcherCode)}
          />
          <label>
            <input
              checked={isChecked}
              onChange={handleCheckboxChange}
              type="checkbox"
            />
            <span className="redeem-code__check-txt">
              {REDEEM_CODE_CONFIRM_MERCHANT}
            </span>
          </label>
          <ButtonForm
            className="redeem-code__btn"
            dataqa="confirm-submit-code"
            disabled={isDisabled}
            onClick={handleConfirmClick}
          >
            {REDEEM_CODE_BTN_CONFIRM}
          </ButtonForm>
        </div>
      )}
      {showRedeem && validationData?.details && (
        <div className="redeem-code__redeem">
          {validationData.details.refundType === 'FULL_CREDIT_REFUND' && (
            <p>{CREDIT_CLAIM_FULL_CREDIT_REFUND}</p>
          )}
          <div className="redeem-code__flex">
            <p>Amount:</p>
            <p className="redeem-code__credit-amount">
              {symbolFromCurrency(validationData?.voucher?.currency)}
              {formatPrice(validationData.details.creditRefundAmount)}
            </p>
          </div>
          <div>
            <CustomNativeSelect
              ariaLabel={CREDIT_CLAIM_REASON_ARIA_LABEL}
              className="primary full-width"
              onChange={handleSelectChange}
              value={refundReasonId}
            >
              <option
                className="menu-select__option"
                key="1"
                label={CREDIT_CLAIM_REASON_SELECT_DEFAULT}
                value="1"
              >
                Select...
              </option>
              {validationData.details.refundReasonsList.map((item) => (
                <option
                  className="menu-select__option"
                  key={item.id}
                  label={item.description}
                  value={item.id}
                >
                  {item.description}
                </option>
              ))}
            </CustomNativeSelect>
          </div>
          {validationData.details.nonRefundableCreditAmount > 0 && (
            <p>
              {CREDIT_CLAIM_NON_REFUNDABLE(
                symbolFromCurrency(validationData?.voucher?.currency) +
                  formatPrice(validationData.details.nonRefundableCreditAmount),
              )}
            </p>
          )}
          <div className="redeem-code__btn-group">
            <ButtonForm
              className="redeem-code__btn"
              onClick={handleCancelClick}
            >
              {REDEEM_CODE_BTN_CANCEL}
            </ButtonForm>
            <ButtonForm
              className="redeem-code__btn redeem-code__last"
              disabled={disableButton}
              onClick={handleRedeemConfirmClick}
              primaryAction={false}
              secondaryButton={SHOW_SECONDARY_BUTTON_REDDEM_FORM}
              tertiaryButton={SHOW_TERTIARY_BUTTON_REDDEM_FORM}
            >
              {REDEEM_CODE_BTN_CONFIRM}
            </ButtonForm>
          </div>
        </div>
      )}
      {showConfirmation && (
        <>
          {towardsDeal && (
            <p
              dangerouslySetInnerHTML={{
                __html: CREDIT_CLAIM_TOWARDS_DEAL(
                  wowcherOrVoucher,
                  symbolFromCurrency(validationData?.voucher?.currency) +
                    formatPrice(validationData.details.creditRefundAmount),
                ),
              }}
            />
          )}
          {!towardsDeal && (
            <>
              <p
                dangerouslySetInnerHTML={{
                  __html: CREDIT_CLAIM_NOT_TOWARDS_DEAL(
                    wowcherOrVoucher,
                    symbolFromCurrency(validationData?.voucher?.currency) +
                      formatPrice(validationData.details.creditRefundAmount),
                  ),
                }}
              />
              <p>{CREDIT_CLAIM_NOT_TOWARDS_DEAL_RETURN(wowcherOrVoucher)}</p>
            </>
          )}
          <ButtonForm className="redeem-code__btn" onClick={handleCancelClick}>
            {REDEEM_CODE_BTN_DONE}
          </ButtonForm>
        </>
      )}
      {hasError && <div className="redeem-code__error">{hasError}</div>}
      <style jsx>{`
        .redeem-code__form {
          position: relative;
        }
        .redeem-code__form :global(.redeem-code__btn) {
          margin-top: 15px;
          width: 100%;
          padding: 24px 16px;
        }
        .redeem-code__label {
          display: inline-block;
          max-width: 100%;
          margin-bottom: 5px;
          font-weight: 700;
          text-transform: capitalize;
        }
        .redeem-code__error {
          font-style: italic;
          color: #eb3f3c;
          font-weight: 700;
        }
        .redeem-code__check-txt {
          padding-left: 7px;
          font-weight: 700;
          font-size: 12px;
        }
        .redeem-code__flex,
        .redeem-code__btn-group {
          display: flex;
        }
        .redeem-code__credit-amount {
          margin-left: auto;
          color: ${theme.colors.primary};
          font-weight: bold;
        }
        .redeem-code__btn-group {
          display: flex;
        }
        .redeem-code__btn-group :global(.redeem-code__last) {
          margin-left: 30px;
          background-color: ${theme.colors.primary};
          color: white;
        }
        .redeem-code__btn-group :global(.redeem-code__last):hover {
          background-color: ${theme.colors.primarydark};
        }
        .redeem-code__btn-primary {
          color: ${theme.colors.primary};
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .redeem-code__btn-group {
            display: block;
          }
          .redeem-code__btn-group :global(.redeem-code__last) {
            margin: 5px 0 0 0;
          }
        }
      `}</style>
    </div>
  );
};

RedeemForm.propTypes = {
  currency: PropTypes.string,
  hasLoadingSpinner: PropTypes.func,
  onCompleteCreditPage: PropTypes.func,
  redeemCreditOnComplete: PropTypes.func,
  showForm: PropTypes.bool,
};

RedeemForm.defaultProps = {
  hasLoadingSpinner: () => {},
  redeemCreditOnComplete: () => {},
};

export default RedeemForm;
